// src/pages/ProductsPage.js
import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { PRODUCT_CATEGORIES } from "../data/products";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getPublicCategories } from "../services/categories.api";
import { useSeo } from "../hooks/useSeo";
import { apiRequest } from "../services/api";

function ProductsPage() {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();
  const navigate = useNavigate();

  const params = useParams();
const [searchParams] = useSearchParams();

const urlCategory = params.slug || null;
const urlSpecial = params.special || null;
const urlSearch = searchParams.get("q");

 const SPECIAL_SECTIONS = ["new-in", "bestsellers", "sale"];
 const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [initialSection, setInitialSection] = useState(() => {
  return (
    urlCategory ||
    urlSpecial ||
    location.state?.section ||
    null
  );
});

const searchQuery = urlSearch || location.state?.search || null;


  const [products, setProducts] = useState([]);
  const [allVariantProducts, setAllVariantProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);

 const [categoryFilter, setCategoryFilter] = useState(() => {
  const section = urlCategory || location.state?.section;
  if (section && !SPECIAL_SECTIONS.includes(section)) {
    return [section];
  }
  return [];
});

  const [sizeFilter, setSizeFilter] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);
  const [heelFilter, setHeelFilter] = useState("all");

  const [sortOption, setSortOption] = useState("bestSelling");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(5000);

  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [gridMode, setGridMode] = useState("two");

const isIOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

const PAGE_SIZE = isIOS ? 14 : isMobile ? 18 : 50;

const [currentPage, setCurrentPage] = useState(1);


  React.useEffect(() => {
    const t = setTimeout(() => setShowWhatsappCTA(true), 1000);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    getAllProducts()
      .then((data) => setAllVariantProducts(data.map(mapBackendProduct)))
      .catch(console.error);
  }, []);

  React.useEffect(() => {
    getPublicCategories().then(setAllCategories).catch(console.error);
  }, []);

  React.useEffect(() => {
  let mounted = true;
  setLoading(true);

  const filters = {};

// 🔍 SEARCH HAS PRIORITY
if (searchQuery) {
  filters.search = searchQuery;
}

else if (initialSection === "sale") {
  filters.sale = "true";
}
else if (initialSection === "new-in") {
  filters.new = "true";
}
else if (initialSection === "bestsellers") {
  filters.bestseller = "true";
}


// 🟢 NORMAL CATEGORY
else if (
  initialSection &&
  categoryFilter.length === 1 &&
  categoryFilter[0] === initialSection
) {
  filters.category = initialSection;
}


  getAllProducts(filters)
    .then((data) => mounted && setProducts(data.map(mapBackendProduct)))
    .finally(() => setLoading(false));

  return () => (mounted = false);
}, [initialSection, categoryFilter, searchQuery]);

  const filteredAndSorted = useMemo(() => {
    let result = [...products];
    
    // 🟢 SPECIAL SECTIONS FILTERING (FRONTEND)
if (initialSection === "sale") {
  result = result.filter(
    (p) =>
      p.onSale === true ||
      p.onSale === 1 ||
      (p.discount && Number(p.discount) > 0)
  );
}

if (initialSection === "new-in") {
  result = result.filter(
    (p) => p.isNew === true || p.isNew === 1
  );
}

if (initialSection === "bestsellers") {
  result = result.filter(
    (p) => p.isBestseller === true || p.isBestseller === 1
  );
}

    if (categoryFilter.length) {
      result = result.filter((p) =>
        p.categories?.some((c) => categoryFilter.includes(c))
      );
    }

    if (sizeFilter.length) {
      result = result.filter((p) =>
        p.sizes?.some((s) => sizeFilter.includes(s.size) && s.inStock)
      );
    }

    if (colorFilter.length) {
      result = result.filter((p) =>
        p.colors?.some((c) => colorFilter.includes(c.name))
      );
    }

    if (priceMax !== null) {
      result = result.filter((p) => {
        const finalPrice = p.discount
          ? Math.round(p.price * (1 - p.discount / 100))
          : p.price;
        return finalPrice >= priceMin && finalPrice <= priceMax;
      });
    }

    if (sortOption === "priceLowHigh") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortOption === "priceHighLow") {
      result.sort((a, b) => b.price - a.price);
    }

    return result;
  }, [
    products,
    categoryFilter,
    sizeFilter,
    colorFilter,
    priceMin,
    priceMax,
    sortOption,
  ]);

const totalPages = Math.max(
  1,
  Math.ceil(filteredAndSorted.length / PAGE_SIZE)
);

const paginatedProducts = useMemo(() => {
  const start = (currentPage - 1) * PAGE_SIZE;
  return filteredAndSorted.slice(start, start + PAGE_SIZE);
}, [filteredAndSorted, currentPage]);

React.useEffect(() => {
  setCurrentPage(1);
}, [filteredAndSorted.length, initialSection, searchQuery]);

  React.useEffect(() => {
  // ✅ If searching, clear category context
  if (searchQuery) {
    setCategoryFilter([]);
    return;
  }

  if (!initialSection) return;

  if (!SPECIAL_SECTIONS.includes(initialSection)) {
    setCategoryFilter([initialSection]);
  } else {
    setCategoryFilter([]);
  }

  setSizeFilter([]);
  setColorFilter([]);
  setHeelFilter("all");
}, [initialSection, searchQuery]);

React.useEffect(() => {
  const nextSection =
    params.slug ||
    params.special ||
    location.state?.section ||
    null;

  setInitialSection(nextSection);
}, [params.slug, params.special, location.state]);


    // ==========================
  // Esiell-style drawer helpers (Lions theme)
  // ==========================
  const TEXT = {
    EN: {
      filters: "Filters",
      category: "Category",
      size: "Size",
      color: "Color",
      price: "Price",
      heelHeight: "Heel height",
      all: "All",
      low: "Low",
      mid: "Mid",
      high: "High",
      appliedFilters: "Applied filters",
      clear: "Clear",
      clearAll: "Clear all",
      apply: "Apply",
    },
    AR: {
      filters: "تصفية",
      category: "الفئة",
      size: "المقاس",
      color: "اللون",
      price: "السعر",
      heelHeight: "ارتفاع الكعب",
      all: "الكل",
      low: "منخفض",
      mid: "متوسط",
      high: "عالٍ",
      appliedFilters: "الفلاتر المفعّلة",
      clear: "مسح",
      clearAll: "مسح الكل",
      apply: "تطبيق",
    },
  }[lang];

  // CATEGORY OPTIONS (for drawer)
  const CATEGORY_OPTIONS = (allCategories || []).map((c) => ({
    key: c.slug,
    labelEN: c.name_en,
    labelAR: c.name_ar,
  }));

  // show heel filter only for heels + new-in (same Esiell behavior)
  const showHeelFilter =
    initialSection === PRODUCT_CATEGORIES.HEELS || initialSection === "new-in";

  // max price for reset / safety
  const maxProductPrice = useMemo(() => {
    return filteredAndSorted.reduce((max, p) => {
      const finalPrice = p.discount
        ? Math.round(p.price * (1 - p.discount / 100))
        : p.price;
      return finalPrice > max ? finalPrice : max;
    }, 0);
  }, [filteredAndSorted]);

  // AVAILABLE SIZES (smart sort)
  const allSizes = useMemo(() => {
    const set = new Set();

    (allVariantProducts || []).forEach((p) =>
      (p.sizes || []).forEach(({ size }) => {
        if (size === null || size === undefined) return;
        if (typeof size === "number" && Number.isNaN(size)) return;
        if (typeof size === "string" && size.trim() === "") return;
        set.add(size);
      })
    );

    const arr = Array.from(set);
    return arr.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isNaN(na) || Number.isNaN(nb))
        return String(a).localeCompare(String(b));
      return na - nb;
    });
  }, [allVariantProducts]);

  // AVAILABLE COLORS (unique by name)
  const allColors = useMemo(() => {
    const c = [];
    (allVariantProducts || []).forEach((p) =>
      (p.colors || []).forEach((col) => {
        if (!c.find((x) => x.name === col.name)) c.push(col);
      })
    );
    return c;
  }, [allVariantProducts]);

  // HELPERS
  const toggleCategory = (key) => {
    setCategoryFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearAllFilters = () => {
    setCategoryFilter([]);
    setSizeFilter([]);
    setColorFilter([]);
    setHeelFilter("all");
    setPriceMin(0);
    setPriceMax(maxProductPrice || 5000);
  };

  // Applied filters chips (Esiell-like)
  const appliedFilters = [];

  if (categoryFilter.length) {
    const labels = CATEGORY_OPTIONS.filter((c) =>
      categoryFilter.includes(c.key)
    )
      .map((c) => (lang === "EN" ? c.labelEN : c.labelAR))
      .join(", ");

    appliedFilters.push({
      id: "category",
      label: `${TEXT.category}: ${labels}`,
      onClear: () => setCategoryFilter([]),
    });
  }


  if (colorFilter.length) {
    appliedFilters.push({
      id: "color",
      label: `${TEXT.color}: ${colorFilter.join(", ")}`,
      onClear: () => setColorFilter([]),
    });
  }

  if (heelFilter !== "all") {
    const heelLabel =
      heelFilter === "low"
        ? TEXT.low
        : heelFilter === "mid"
        ? TEXT.mid
        : TEXT.high;

    appliedFilters.push({
      id: "heel",
      label: `${TEXT.heelHeight}: ${heelLabel}`,
      onClear: () => setHeelFilter("all"),
    });
  }

  if (priceMin > 0 || priceMax < (maxProductPrice || 5000)) {
    appliedFilters.push({
      id: "price",
      label: `${TEXT.price}: ${Number(priceMin) || 0} – ${Number(priceMax) || 0}`,
      onClear: () => {
        setPriceMin(0);
        setPriceMax(maxProductPrice || 5000);
      },
    });
  }

const pageTitle = useMemo(() => {
  if (!initialSection) {
    return lang === "EN" ? "Products" : "المنتجات";
  }

  // Special sections
  if (initialSection === "new-in") {
    return lang === "EN" ? "New Collection" : "مجموعة جديدة";
  }

  if (initialSection === "sale") {
    return lang === "EN" ? "On Sale" : "تخفيضات";
  }

  if (initialSection === "bestsellers") {
    return lang === "EN" ? "Bestsellers" : "الأكثر مبيعًا";
  }

  // Category from API
  const cat = allCategories.find((c) => c.slug === initialSection);
  if (cat) {
    return lang === "EN" ? cat.name_en : cat.name_ar;
  }

  // Fallback
  return String(initialSection);
}, [initialSection, allCategories, lang]);

const seoTitle = useMemo(() => {
  if (searchQuery) {
    return lang === "EN"
      ? `Search results for "${searchQuery}" | Lions`
      : `نتائج البحث عن "${searchQuery}" | لايونز`;
  }

  if (initialSection === "new-in") {
    return lang === "EN"
      ? "New Tactical Arrivals | Lions"
      : "وصول جديد تكتيكي | لايونز";
  }

  if (initialSection === "sale") {
    return lang === "EN"
      ? "Tactical Gear On Sale | Lions"
      : "معدات تكتيكية بخصومات | لايونز";
  }

  if (initialSection === "bestsellers") {
    return lang === "EN"
      ? "Best Selling Tactical Gear | Lions"
      : "الأكثر مبيعًا من المعدات التكتيكية | لايونز";
  }

  return lang === "EN"
    ? `${pageTitle} | Lions Tactical Gear`
    : `${pageTitle} | لايونز`;
}, [initialSection, searchQuery, pageTitle, lang]);

const seoDescription = useMemo(() => {
  if (searchQuery) {
    return lang === "EN"
      ? `Browse tactical gear matching "${searchQuery}". High-performance equipment by Lions.`
      : `تصفح معدات تكتيكية مطابقة لبحثك "${searchQuery}". أداء عالٍ من لايونز.`;
  }

  return lang === "EN"
    ? `Browse ${pageTitle.toLowerCase()} including tactical gear, uniforms, belts, holsters and accessories by Lions.`
    : `تصفح ${pageTitle} من معدات تكتيكية وملابس عسكرية وإكسسوارات من لايونز.`;
}, [searchQuery, pageTitle, lang]);

useSeo(
  "products",
  initialSection || "all",
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoTitle,
    description: seoDescription,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filteredAndSorted.slice(0, 12).map((p, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url:
          typeof window !== "undefined"
            ? `${window.location.origin}/product/${p.slug}`
            : "",
        name: lang === "EN" ? p.nameEN : p.nameAR,
      })),
    },
  }
);


  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-[#0F1A12] text-white">
        Loading products…
      </div>
    );
  }

  return (
     <div className="w-full min-h-screen flex flex-col overflow-x-hidden bg-[#0F1A12] text-white">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <div
  className="w-full min-h-screen pt-[95px] px-4 pb-24 max-w-6xl mx-auto"
  dir={lang === "AR" ? "rtl" : "ltr"}
>
        <button
          onClick={() => navigate(-1)}
          style={lang === "AR" ? { right: 16 } : { left: 16 }}
          className="absolute top-[110px] text-3xl opacity-60"
        >
          ×
        </button>

        <h1 className="text-xl font-semibold text-center tracking-wide mt-2">
  {pageTitle}
</h1>
<div className="h-[2px] w-12 bg-[#2E5A2A] mx-auto mt-2 mb-4"></div>
 <div className="flex gap-2 mb-3">
  {/* FILTER */}
  <button
    onClick={() => setIsFilterOpen(true)}
    className="
      flex-1 flex items-center justify-center gap-2
      bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
 rounded-md
      py-2.5 text-xs font-semibold tracking-wide
      active:scale-[0.98]
    "
  >
    <span>{lang === "EN" ? "FILTERS" : "تصفية"}</span>
    <span className="flex flex-col gap-[2px]">
      <span className="w-3 h-[1.5px] bg-white rounded-full" />
      <span className="w-4 h-[1.5px] bg-white rounded-full" />
      <span className="w-2 h-[1.5px] bg-white rounded-full" />
    </span>
  </button>

  {/* SORT */}
  <div className="relative flex-1">
    <button
      onClick={() => setIsSortOpen((p) => !p)}
      className="
        w-full flex items-center justify-center gap-2
        rounded-md
        py-2.5 text-xs font-semibold tracking-wide
        bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
      "
    >
      <span>{lang === "EN" ? "SORT BY" : "ترتيب حسب"}</span>
      <span className="text-[10px]">{isSortOpen ? "▲" : "▼"}</span>
    </button>

{isSortOpen && (
  <div
    className="
      absolute right-0 mt-1 w-44
      bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
      rounded-md shadow-lg z-20 text-xs
    "
    style={lang === "AR" ? { left: 0, right: "auto" } : {}}
  >
    <button
      onClick={() => {
        setSortOption("bestSelling");
        setIsSortOpen(false);
      }}
      className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
        sortOption === "bestSelling" ? "font-semibold text-[#2E5A2A]" : ""
      }`}
    >
      {lang === "EN" ? "Best Selling" : "الأكثر مبيعًا"}
    </button>

    <button
      onClick={() => {
        setSortOption("priceLowHigh");
        setIsSortOpen(false);
      }}
      className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
        sortOption === "priceLowHigh" ? "font-semibold text-[#2E5A2A]" : ""
      }`}
    >
      {lang === "EN" ? "Price: Low to High" : "السعر: من الأقل للأعلى"}
    </button>

    <button
      onClick={() => {
        setSortOption("priceHighLow");
        setIsSortOpen(false);
      }}
      className={`w-full text-left px-3 py-2 hover:bg-[#2E5A2A]/20 ${
        sortOption === "priceHighLow" ? "font-semibold text-[#2E5A2A]" : ""
      }`}
    >
      {lang === "EN" ? "Price: High to Low" : "السعر: من الأعلى للأقل"}
    </button>
  </div>
  )}
  </div>
 </div>
 {/* COUNT + GRID TOGGLE */}
<div className="flex items-center justify-between mb-4">
  <span className="text-[11px] text-white/60">
    {filteredAndSorted.length} {lang === "EN" ? "products" : "منتج"}
  </span>

  <div className="flex items-center gap-1">
    <button
      onClick={() => setGridMode("two")}
      className={`
        w-7 h-7 border rounded-md flex items-center justify-center
        ${gridMode === "two" ? "bg-[#2E5A2A] text-white border-[#2E5A2A]" : "border-white/30"}
      `}
      aria-label="Grid 2 per row"
      title="Grid"
    >
      <div className="grid grid-cols-2 grid-rows-2 gap-[2px] w-3 h-3">
        <span className="bg-current" />
        <span className="bg-current" />
        <span className="bg-current" />
        <span className="bg-current" />
      </div>
    </button>

    <button
      onClick={() => setGridMode("one")}
      className={`
        w-7 h-7 border rounded-md flex items-center justify-center
        ${gridMode === "one" ? "bg-[#2E5A2A] text-white border-[#2E5A2A]" : "border-white/30"}
      `}
      aria-label="Grid 1 per row"
      title="List"
    >
      <div className="flex flex-col gap-[2px] w-3">
        <span className="h-[3px] bg-current rounded-sm" />
        <span className="h-[3px] bg-current rounded-sm" />
        <span className="h-[3px] bg-current rounded-sm" />
      </div>
    </button>
  </div>
</div>
        {filteredAndSorted.length === 0 && (
          <p className="mt-10 text-center text-sm text-white/60">
            No products found.
          </p>
        )}

        <div
  className={`grid ${
    gridMode === "two"
      ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
  } gap-3 md:gap-4`}
>
{paginatedProducts.map((p) => (
  <ProductCard key={p.id} product={p} />
))}

        </div>

{/* PAGINATION */}
{totalPages > 1 && (
  <div className="flex justify-center gap-2 mt-8">
    {Array.from({ length: totalPages }).map((_, i) => (
      <button
        key={i}
        onClick={() => {
          setCurrentPage(i + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        className={`w-9 h-9 text-xs font-semibold border ${
          currentPage === i + 1
            ? "bg-[#2E5A2A]"
            : "border-[#2E5A2A]"
        }`}
      >
        {i + 1}
      </button>
    ))}
  </div>
)}

        {/* EXPLORE ALL PRODUCTS */}
<div className="mt-10 flex justify-center">
  <button
    onClick={() => navigate("/all-products")}
    className="
      px-10 py-3
      rounded-md
      text-xs font-semibold tracking-widest
      border border-[#2E5A2A]/60
      text-[#9EFFA1]
      hover:bg-[#2E5A2A]/20
      transition
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>

      </div>

{/* ================= FILTER DRAWER (LEFT) ================= */}
<div
  className={`
    fixed inset-0 z-[9999]
    transition-opacity duration-300
    ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
  `}
>
  {/* BACKDROP */}
  <div
    className="absolute inset-0 bg-black/60"
    onClick={() => setIsFilterOpen(false)}
  />

  {/* PANEL */}
  <div
    className={`
      absolute top-0 bottom-0 left-0
      w-[90%] max-w-sm
      bg-gradient-to-b from-[#0F1A12] to-[#0B140E]
text-white
border-r border-[#2E5A2A]/40
shadow-[4px_0_30px_rgba(0,0,0,0.7)]
      transform transition-transform duration-300
      ${isFilterOpen ? "translate-x-0" : "-translate-x-full"}
    `}
    dir={lang === "AR" ? "rtl" : "ltr"}
    style={lang === "AR" ? { left: "auto", right: 0 } : {}}
  >
    {/* HEADER */}
    <div className="flex items-center justify-between px-4 py-4
border-b border-[#2E5A2A]/40
bg-[#101C14]
sticky top-0 z-10">

      <span className="text-sm font-semibold">
        {TEXT.filters}
      </span>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="text-2xl opacity-70 hover:opacity-100"
      >
        ×
      </button>
    </div>

    {/* CONTENT */}
    <div className="h-[calc(100%-110px)] overflow-y-auto px-4 pt-3 pb-24 text-sm">

      {/* APPLIED FILTERS */}
      {appliedFilters.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#9EFFA1]">
              {TEXT.appliedFilters}
            </span>
            <button
              onClick={clearAllFilters}
              className="text-[11px] underline text-white/60 hover:text-white"
            >
              {TEXT.clearAll}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {appliedFilters.map((f) => (
              <button
                key={f.id}
                onClick={f.onClear}
                className="
                  flex items-center gap-1
                  px-2 py-[3px]
                  rounded-full
                  bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition

                  text-[11px]
                "
              >
                <span>{f.label}</span>
                <span>×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORY */}
      <DrawerSection title={TEXT.category}>
        <div className="space-y-2 mt-2">
          {CATEGORY_OPTIONS.map((c) => {
            const checked = categoryFilter.includes(c.key);
            return (
              <label key={c.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCategory(c.key)}
                  className="w-4 h-4 accent-[#2E5A2A]"
                />
                <span>{lang === "EN" ? c.labelEN : c.labelAR}</span>
              </label>
            );
          })}
        </div>
      </DrawerSection>

      {/* COLOR */}
      <DrawerSection title={TEXT.color}>
        <div className="flex flex-wrap gap-3 mt-2">
          {allColors.map((c) => (
            <button
              key={c.name}
              onClick={() =>
                setColorFilter((prev) =>
                  prev.includes(c.name)
                    ? prev.filter((x) => x !== c.name)
                    : [...prev, c.name]
                )
              }
              className="flex flex-col items-center gap-1 text-[10px]"
            >
              <span
                className={`
                  w-7 h-7 rounded-full border
                  ${colorFilter.includes(c.name)
                    ? "border-[#9EFFA1]"
                    : "border-white/30"}
                `}
                style={{ backgroundColor: c.code }}
              />
              <span className="text-white/70">{c.name}</span>
            </button>
          ))}
        </div>
      </DrawerSection>

      {/* HEEL HEIGHT */}
      {showHeelFilter && (
        <DrawerSection title={TEXT.heelHeight}>
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { key: "all", label: TEXT.all },
              { key: "low", label: TEXT.low },
              { key: "mid", label: TEXT.mid },
              { key: "high", label: TEXT.high },
            ].map((h) => (
              <button
                key={h.key}
                onClick={() => setHeelFilter(h.key)}
                className={`
                  px-3 py-1 rounded-full border text-xs
                  ${
                    heelFilter === h.key
                      ? "bg-[#2E5A2A] text-white border-[#2E5A2A]"
                      : "border-white/30 text-white"
                  }
                `}
              >
                {h.label}
              </button>
            ))}
          </div>
        </DrawerSection>
      )}

      {/* PRICE */}
      <DrawerSection title={TEXT.price}>
        <div className="mt-3 space-y-3">
          <div className="flex gap-3">
            <input
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(Number(e.target.value) || 0)}
              className="w-full bg-[#121F17] bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
 rounded px-2 py-1 text-xs"
            />
            <input
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(Number(e.target.value) || 0)}
              className="w-full bg-[#121F17] bg-[#132117]
border border-[#2E5A2A]/50
hover:bg-[#1B2F22]
transition
rounded px-2 py-1 text-xs"
            />
          </div>
        </div>
      </DrawerSection>
      {/* EXPLORE ALL */}
<div className="mt-6 px-1">
  <button
    onClick={() => {
      setIsFilterOpen(false);
      navigate("/all-products");
    }}
    className="
      w-full py-3
      rounded-md
      text-xs font-semibold tracking-widest
      border border-[#2E5A2A]/50
      text-[#9EFFA1]
      hover:bg-[#2E5A2A]/20
      transition
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>
    </div>

    {/* FOOTER BUTTONS */}
    <div className="absolute bottom-0 left-0 right-0 border-t border-[#2E5A2A]/30 bg-[#0F1A12] px-4 py-4 flex gap-3 bg-[#0C1610]
">
      <button
        onClick={clearAllFilters}
        className="flex-1 py-2 border border-white/30 rounded-md text-xs"
      >
        {TEXT.clear.toUpperCase()}
      </button>
      <button
        onClick={() => setIsFilterOpen(false)}
        className="flex-1 py-2 rounded-md text-xs bg-[#2E5A2A]"
      >
        {TEXT.apply.toUpperCase()}
      </button>
    </div>
  </div>
</div>


      {/* WHATSAPP CTA */}
      {showWhatsappCTA && (
        <div className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2">
          <button
            onClick={() => setShowWhatsappCTA(false)}
            className="w-7 h-7 rounded-full bg-white text-black text-sm"
          >
            ×
          </button>
          <a
            href="https://wa.me/96170203205"
            target="_blank"
            rel="noreferrer"
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "#25D366" }}
          >
            <img
              src="/icons/whatsapp.png"
              alt="WhatsApp"
              className="w-7 h-7"
            />
          </a>
        </div>
      )}

      {showScrollTop && (
        <button
          onClick={() =>
            window.scrollTo({ top: 0, behavior: "smooth" })
          }
          className="fixed top-24 left-1/2 -translate-x-1/2 w-12 h-12 bg-white text-black rounded-full"
        >
          ↑
        </button>
      )}
    </div>
  );
}

function DrawerSection({ title, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#2E5A2A]/25 py-3">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between text-sm font-semibold tracking-wide text-[#D6E5D9]"
      >
        <span>{title}</span>
        <span className="text-xs opacity-70">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export default ProductsPage;
